import { useCallback } from 'react';
import { cn } from '@/lib/cn';
import { normalizeReminderOffsets } from '@/lib/eventReminders';

const PRESETS: { minutes: number; label: string }[] = [
  { minutes: 30, label: '30 min' },
  { minutes: 15, label: '15 min' },
  { minutes: 5, label: '5 min' },
  { minutes: 0, label: 'At start' },
];

type Props = {
  value: number[];
  onChange: (next: number[]) => void;
  disabled?: boolean;
  className?: string;
  /** Hide the inline label when the parent already shows a title. */
  hideLabel?: boolean;
};

/**
 * Multi-select reminder offsets (minutes before start; `0` = at start).
 */
export function EventRemindersPicker({ value, onChange, disabled, className, hideLabel }: Props) {
  const toggle = useCallback(
    (minutes: number) => {
      const set = new Set(value);
      if (set.has(minutes)) set.delete(minutes);
      else set.add(minutes);
      onChange(normalizeReminderOffsets([...set]));
    },
    [onChange, value]
  );

  const setNone = useCallback(() => onChange([]), [onChange]);

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between gap-2">
        {hideLabel ? <span /> : <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Reminders</span>}
        <button
          type="button"
          disabled={disabled || value.length === 0}
          onClick={setNone}
          className="text-[11px] font-semibold text-slate-400 transition-colors hover:text-[#5B47E8] disabled:opacity-40"
        >
          None
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {PRESETS.map(({ minutes, label }) => {
          const active = value.includes(minutes);
          return (
            <button
              key={minutes}
              type="button"
              disabled={disabled}
              onClick={() => toggle(minutes)}
              className={cn(
                'rounded-full px-4 py-2 text-xs font-semibold transition-colors',
                active
                  ? 'bg-gradient-to-r from-[#5B6CF6] to-[#8B5CF6] text-white'
                  : 'bg-white text-slate-600 hover:bg-[#EEF2FF] hover:text-[#5B47E8] dark:bg-white/5 dark:text-slate-300 dark:hover:bg-[#1C1F3F] dark:hover:text-[#8B7CFF]',
                disabled && 'opacity-50'
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500 dark:text-slate-400">
        Push notifications require browser permission. No reminders are sent if none are selected.
      </p>
    </div>
  );
}
